/**
 * Extracts data from the currently rendered HTML DOM results.
 * @param {boolean} summaryOnly - If true, only includes base stats, multipliers, and D/A vs D
 */
function extractDataFromDOM(summaryOnly = false) {
    const resultsContent = document.getElementById('resultsContent');
    const extractedData = [];
    let currentSlot = "Results";

    Array.from(resultsContent.children).forEach(child => {
        // Exclude export actions div itself if it somehow gets parsed
        if (child.classList.contains('export-actions')) return;

        if (child.tagName === 'H3') {
            const titleSpan = child.querySelector('span');
            currentSlot = titleSpan ? titleSpan.textContent.trim() : child.textContent.trim();
        } else if (child.classList.contains('comparison-grid')) {
            const scenarios = child.querySelectorAll('.scenario-container');
            const slotScenarios = [];

            scenarios.forEach(scene => {
                const titleEl = scene.querySelector('.scenario-title');
                if (!titleEl) return;
                const title = titleEl.textContent;

                const table = scene.querySelector('.comparison-table');
                if (!table) return;

                const theadMatches = table.querySelectorAll('thead th');
                const headers = Array.from(theadMatches).map(th => th.textContent.trim());

                const rows = [];
                const tbodyRows = table.querySelectorAll('tbody tr');
                tbodyRows.forEach(tr => {
                    const rowType = tr.dataset.statType;

                    if (summaryOnly && rowType === 'stacked') {
                        // Keep 'Dragon Attack vs Dragons' (which might be abbreviated to 'DvD')
                        // Also keep Capacity/Troop stats for the Caps scenarios
                        const firstCellText = tr.querySelector('td')?.textContent.trim() || '';
                        const lowerLabel = firstCellText.toLowerCase();
                        
                        const isCapacityStat = lowerLabel.includes('cap') || lowerLabel.includes('troops vs sop');

                        if (firstCellText !== 'Dragon Attack vs Dragons' && firstCellText !== 'DvD' && !isCapacityStat) {
                            return; // Skip detailed stacked stats for summary
                        }
                    }

                    const cells = tr.querySelectorAll('td');
                    if (cells.length === 1) {
                        rows.push({ type: 'header', text: cells[0].textContent.trim() });
                    } else if (cells.length >= 4) {
                        const getCellText = (td) => {
                            const span = td.querySelector('.stat-value-container > span');
                            if (span) {
                                return span.textContent.trim();
                            }
                            return td.textContent.trim();
                        };

                        let rowLabel = getCellText(cells[0]);

                        // Apply the exact same abbreviations the mobile UI uses for consistency
                        if (rowLabel === 'Dragon Attack vs Dragons') {
                            rowLabel = 'DvD';
                        }

                        if (summaryOnly && rowType === 'multipliers') {
                            rowLabel = rowLabel.replace(/^(?:Attack|Defense)\s+(vs\s+.*)/i, '$1');
                        }

                        rowLabel = rowLabel.replace(/Dragon/g, 'Drag');
                        rowLabel = rowLabel.replace(/While Marching vs Player/g, 'Marching');
                        rowLabel = rowLabel.replace(/Marcher (.*?) vs Player/g, '$1 Marching');
                        rowLabel = rowLabel.replace(/vs Player at SOP/g, 'SOP');
                        rowLabel = rowLabel.replace(/vs Player/g, 'vs PL');

                        rows.push({
                            type: 'data',
                            stat: rowLabel,
                            s1: getCellText(cells[1]),
                            s2: getCellText(cells[2]),
                            diff: getCellText(cells[3])
                        });
                    }
                });

                slotScenarios.push({
                    title,
                    headers,
                    rows
                });
            });

            if (slotScenarios.length > 0) {
                extractedData.push({
                    slot: currentSlot,
                    scenarios: slotScenarios
                });
            }
        }
    });

    return extractedData;
}

export function exportToCSV() {
    const data = extractDataFromDOM();
    if (data.length === 0) {
        alert("No results to export.");
        return;
    }

    let csvContent = "\uFEFFsep=,\r\n"; // Add BOM and explicitly define separator for Excel compatibility

    const comparisonTitle = document.getElementById('comparisonTitle')?.value || window._savedComparisonTitle || '';
    if (comparisonTitle.trim()) {
        let safeTitle = comparisonTitle.trim().replace(/"/g, '""');
        // Prevent CSV formula injection
        if (/^[=+\-@\t\r]/.test(safeTitle)) {
            safeTitle = "'" + safeTitle;
        }
        csvContent += `"${safeTitle}"\r\n\r\n`;
    }

    data.forEach(slotData => {
        csvContent += `${slotData.slot}\r\n`;

        slotData.scenarios.forEach(scene => {
            csvContent += `\r\n${scene.title}\r\n`;

            if (scene.headers.length >= 4) {
                csvContent += `"${scene.headers[0]}","${scene.headers[1]}","${scene.headers[2]}","${scene.headers[3]}"\r\n`;
            }

            scene.rows.forEach(row => {
                if (row.type === 'header') {
                    csvContent += `"${row.text}"\r\n`;
                } else {
                    csvContent += `"${row.stat}","${row.s1}","${row.s2}","${row.diff}"\r\n`;
                }
            });
        });

        csvContent += "\r\n";
    });

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "gearset_comparison.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

function generateDiscordMarkdown(data, title, filters = null) {
    const categories = [
        { name: 'Totals', slots: ['ALL EQUIPMENT'] },
        { name: 'Equipment', slots: ['HELMET', 'CHEST', 'PANTS', 'BOOTS', 'WEAPON', 'RING'] },
        { name: 'Trinkets', slots: ['TRINKET 1', 'TRINKET 2'] },
        { name: 'Pit Gear', slots: ['SADDLE', 'CHANFRON', 'PEYTRAL'] }
    ];

    let mdContent = title.trim() ? `**${title.trim()}**\n\n` : "**Gearset Results**\n\n";

    categories.forEach(cat => {
        if (filters && cat.name === 'Totals' && !filters.includes('totals')) return;

        let catData = data.filter(d => cat.slots.includes(d.slot.toUpperCase()));
        if (catData.length === 0) return;

        let uniformA = true, uniformB = true;
        let firstNameA = null, firstQualA = null;
        let firstNameB = null, firstQualB = null;

        catData.forEach(slotData => {
            let qualA = "", qualB = "", nameA = "", nameB = "";
            if (slotData.scenarios.length > 0) {
                let h1 = (slotData.scenarios[0].headers[1] || "").replace(/\s+/g, ' ').trim();
                let h2 = (slotData.scenarios[0].headers[2] || "").replace(/\s+/g, ' ').trim();

                let m1 = h1.match(/(.*?)\s*(\([^)]+\))$/);
                if (m1) { nameA = m1[1].trim(); qualA = m1[2].trim(); } else { nameA = h1.trim(); }

                let m2 = h2.match(/(.*?)\s*(\([^)]+\))$/);
                if (m2) { nameB = m2[1].trim(); qualB = m2[2].trim(); } else { nameB = h2.trim(); }
            }
            slotData.parsedHeader = { nameA, qualA, nameB, qualB };

            if (firstNameA === null) firstNameA = nameA;
            if (firstQualA === null) firstQualA = qualA;
            if (firstNameB === null) firstNameB = nameB;
            if (firstQualB === null) firstQualB = qualB;

            if (nameA !== firstNameA || qualA !== firstQualA) uniformA = false;
            if (nameB !== firstNameB || qualB !== firstQualB) uniformB = false;
        });

        let isUniform = uniformA && uniformB && cat.name !== 'Totals';
        let renderedContentForCat = "";

        if (isUniform && cat.name !== 'Totals') {
            let topA = firstNameA || "A";
            if (firstQualA) topA += ` ${firstQualA}`;
            let topB = firstNameB || "B";
            if (firstQualB) topB += ` ${firstQualB}`;
            renderedContentForCat += `**${cat.name}**\nA: ${topA} | B: ${topB}\n\n`;
        } else {
            renderedContentForCat += `**${cat.name}**\n\n`;
        }

        let addedAnySlot = false;

        catData.forEach(slotData => {
            let slotContent = `**[ ${slotData.slot} ]**\n`;

            if (!isUniform && slotData.parsedHeader && cat.name !== 'Totals') {
                let na = slotData.parsedHeader.nameA || "A";
                let qa = slotData.parsedHeader.qualA || "";
                let nb = slotData.parsedHeader.nameB || "B";
                let qb = slotData.parsedHeader.qualB || "";
                let labelA = qa ? `${na} ${qa}` : na;
                let labelB = qb ? `${nb} ${qb}` : nb;
                if (labelA !== "A" || labelB !== "B") {
                    slotContent += `${labelA} vs ${labelB}\n`;
                }
            }

            let addedAnyScene = false;
            slotData.scenarios.forEach(scene => {
                if (filters && cat.name !== 'Totals' && !filters.includes(scene.title)) return;

                slotContent += `*${scene.title}*\n`;
                scene.rows.forEach(r => {
                    if (r.type === 'header') {
                        slotContent += `**${r.text}**\n`;
                    } else if (r.type === 'data') {
                        let diffStr = r.diff;
                        if (diffStr.startsWith('+')) diffStr = '＋' + diffStr.substring(1);
                        else if (diffStr.startsWith('-')) diffStr = '－' + diffStr.substring(1);
                        slotContent += `${r.stat}: ${r.s1} | ${r.s2} (${diffStr})\n`;
                    }
                });
                slotContent += "\n";
                addedAnyScene = true;
            });

            if (addedAnyScene) {
                renderedContentForCat += slotContent;
                addedAnySlot = true;
            }
        });

        if (addedAnySlot) {
            mdContent += renderedContentForCat;
        }
    });

    return mdContent;
}

async function performShareOrCopy(mdContent, isOverLimit) {
    const shareData = {
        title: 'Gearset Comparison',
        text: mdContent
    };

    const limitWarning = isOverLimit ? `\n\nNote: The text is ${mdContent.length} characters long, which exceeds Discord's 2,000 character limit. It may be cut off.` : "";

    let copied = false;
    try {
        await navigator.clipboard.writeText(mdContent);
        copied = true;
    } catch (err) {
        console.warn("Initial clipboard copy failed", err);
    }

    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        try {
            if (isOverLimit) {
                alert(`Warning: The generated text is ${mdContent.length} characters, exceeding Discord's limit. It will be shared, but may be cut off.`);
            }
            await navigator.share(shareData);
            return;
        } catch (err) {
            console.warn("Share failed or was aborted:", err);
            if (err.name === 'AbortError') return;
        }
    }

    if (copied) {
        alert("Results copied to clipboard! You can now paste them directly into Discord." + limitWarning);
    } else {
        alert("Failed to copy to clipboard. Ensure you are on HTTPS or localhost (or check console).");
        console.log(mdContent);
    }
}

function showDiscordExportFilterModal(data, comparisonTitle) {
    let modal = document.getElementById('discordExportFilterModal');
    if (!modal) {
        modal = document.createElement('div');
        modal.id = 'discordExportFilterModal';
        modal.className = 'modal-backdrop';
        modal.innerHTML = `
            <div class="modal-content" style="max-width: 450px;">
                <div class="modal-header">
                    <h2>Share Text Options</h2>
                    <button type="button" class="modal-close-btn" aria-label="Close modal" title="Close">&times;</button>
                </div>
                <div class="modal-body">
                    <p class="negative" style="background: rgba(244, 67, 54, 0.1); border: 1px solid rgba(244, 67, 54, 0.3); padding: 0.75rem; border-radius: 8px; margin-bottom: 1.5rem; font-size: 0.9rem;">
                        <strong>Warning:</strong> The generated text exceeds Discord's 2,000 character limit. Please select which sections to include:
                    </p>
                    <div id="discordExportCheckboxes" class="scenario-toggles" style="display: flex; flex-direction: column; align-items: flex-start; gap: 0.75rem; margin-bottom: 1.5rem;">
                    </div>
                    <button type="button" id="discordExportConfirmBtn" class="submit-btn" style="width: 100%; max-width: none;">Continue</button>
                </div>
            </div>
        `;
        document.body.appendChild(modal);

        modal.querySelector('.modal-close-btn').onclick = () => closeModal('discordExportFilterModal');
        modal.addEventListener('click', (e) => {
            if (e.target === modal) closeModal('discordExportFilterModal');
        });
    }

    const cbContainer = modal.querySelector('#discordExportCheckboxes');
    cbContainer.innerHTML = '';

    const hasTotals = data.some(d => d.slot.toUpperCase() === 'TOTALS (ALL EQUIPMENT)');
    if (hasTotals) {
        const lbl = document.createElement('label');
        lbl.className = 'toggle-label';
        lbl.style.width = '100%';
        lbl.innerHTML = `<input type="checkbox" value="totals" checked><span>Totals (Overview)</span>`;
        cbContainer.appendChild(lbl);
    }

    const individualScenarios = new Set();
    data.forEach(d => {
        if (d.slot.toUpperCase() !== 'TOTALS (ALL EQUIPMENT)') {
            d.scenarios.forEach(scene => individualScenarios.add(scene.title));
        }
    });

    individualScenarios.forEach(scene => {
        const lbl = document.createElement('label');
        lbl.className = 'toggle-label';
        lbl.style.width = '100%';
        lbl.innerHTML = `<input type="checkbox" value="${scene}" checked><span>Equipment - ${scene}</span>`;
        cbContainer.appendChild(lbl);
    });

    const confirmBtn = modal.querySelector('#discordExportConfirmBtn');
    const newConfirmBtn = confirmBtn.cloneNode(true);
    confirmBtn.parentNode.replaceChild(newConfirmBtn, confirmBtn);

    newConfirmBtn.onclick = async () => {
        const checkboxes = cbContainer.querySelectorAll('input:checked');
        const selectedFilters = Array.from(checkboxes).map(cb => cb.value);

        const filteredMarkdown = generateDiscordMarkdown(data, comparisonTitle, selectedFilters);

        closeModal('discordExportFilterModal');
        await performShareOrCopy(filteredMarkdown, filteredMarkdown.length > 1950);
    };

    modal.classList.add('visible');
    document.body.classList.add('no-scroll');
}

function closeModal(modalId) {
    const modal = document.getElementById(modalId);
    if (modal) {
        modal.classList.remove('visible');
        document.body.classList.remove('no-scroll');
    }
}

export async function exportToDiscord() {
    const data = extractDataFromDOM(true);
    if (data.length === 0) {
        alert("No results to export.");
        return;
    }

    const comparisonTitle = document.getElementById('comparisonTitle')?.value || window._savedComparisonTitle || '';
    const fullMarkdown = generateDiscordMarkdown(data, comparisonTitle);

    if (fullMarkdown.length > 1950) {
        showDiscordExportFilterModal(data, comparisonTitle);
    } else {
        await performShareOrCopy(fullMarkdown, false);
    }
}

export async function exportSlotToDiscord(slotName) {
    let data = extractDataFromDOM(true);
    if (data.length === 0) {
        alert("No results to export.");
        return;
    }

    if (slotName !== 'totals') {
        const targetSlotName = slotName.replace('trinket', 'Trinket ').toUpperCase();
        data = data.filter(d => d.slot.toUpperCase() === targetSlotName);
    }

    if (data.length === 0) {
        alert("No results for this slot to export.");
        return;
    }

    const comparisonTitle = document.getElementById('comparisonTitle')?.value || window._savedComparisonTitle || '';
    const fullMarkdown = generateDiscordMarkdown(data, comparisonTitle);

    if (fullMarkdown.length > 1950) {
        showDiscordExportFilterModal(data, comparisonTitle);
    } else {
        await performShareOrCopy(fullMarkdown, false);
    }
}

export function exportSlotToCSV(slotName) {
    let data = extractDataFromDOM();
    if (data.length === 0) {
        alert("No results to export.");
        return;
    }

    if (slotName !== 'totals') {
        const targetSlotName = slotName.replace('trinket', 'Trinket ').toUpperCase();
        data = data.filter(d => d.slot.toUpperCase() === targetSlotName);
    }

    if (data.length === 0) {
        alert("No results for this slot to export.");
        return;
    }

    let csvContent = "\uFEFFsep=,\r\n";

    const comparisonTitle = document.getElementById('comparisonTitle')?.value || window._savedComparisonTitle || '';
    if (comparisonTitle.trim()) {
        let safeTitle = comparisonTitle.trim().replace(/"/g, '""');
        if (/^[=+\-@\t\r]/.test(safeTitle)) {
            safeTitle = "'" + safeTitle;
        }
        csvContent += `"${safeTitle}"\r\n\r\n`;
    }

    data.forEach(slotData => {
        csvContent += `${slotData.slot}\r\n`;

        slotData.scenarios.forEach(scene => {
            csvContent += `\r\n${scene.title}\r\n`;

            if (scene.headers.length >= 4) {
                csvContent += `"${scene.headers[0]}","${scene.headers[1]}","${scene.headers[2]}","${scene.headers[3]}"\r\n`;
            }

            scene.rows.forEach(row => {
                if (row.type === 'header') {
                    csvContent += `"${row.text}"\r\n`;
                } else {
                    csvContent += `"${row.stat}","${row.s1}","${row.s2}","${row.diff}"\r\n`;
                }
            });
        });

        csvContent += "\r\n";
    });

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const fileSuffix = slotName === 'totals' ? 'totals' : slotName.toLowerCase();
    link.download = `gearset_comparison_${fileSuffix}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}
